/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
    utils as commonUtils,
    hooks as commonHooks,
    analytics,
    types as commonTypes,
} from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import Text from './Text';
import textStyles, { themeTextStyles } from './Text.styles';

export interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

export interface AnalyticsItem {
    name: string;
    value: string;
}

export interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    textInput: string;
    functionToTransform?: string;
    isTransformData?: boolean;
    typographyClass?: string;
    disableTabStaticData?: boolean;
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
    truncateTextAt?: number;
    readMoreText?: string;
    readLessText?: string;
    useLowerCase?: boolean;
}

const TextWrapper = (props: commonTypes.WidgetInstanceProps): JSX.Element => {
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        textInput,
        readLessText,
        readMoreText,
        useAnalytics,
        dynamicEvents,
        truncateTextAt,
        useLowerCase,
        ...rest
    } = widgetProperties || {};
    const { data } = props;
    const theme = data?.themeSelected || props?.theme;
    const i18n = commonHooks.useLocalization();
    const tracking = analytics.useTracking();
    const annotator = commonHooks.useAnnotator();

    let isRender = {
        text: true,
        transformedData: data,
        textInput,
        readMoreText,
        readLessText,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedData =
            rest?.isTransformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, rest?.functionToTransform, {
                ...data,
                textInput,
                readMoreText,
                readLessText,
                annotator,
            });

        if (transformedData) {
            isRender = { ...isRender, ...transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    const boundText =
        i18n.format((isRender.textInput as string) || '', isRender.transformedData) || '';

    const boundReadMoreText = i18n.format(isRender.readMoreText || '', isRender.transformedData);
    const boundReadLessText = i18n.format(isRender.readLessText || '', isRender.transformedData);

    const onTriggerTracking = (event: string, customValue?: string) => {
        tracking({
            event: event,
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            analyticsCustomValue: customValue || '',
            useLowerCase: useLowerCase,
        });
    };

    return isRender.text ? (
        <div
            id={`text-widget-id-${props.widgetInstanceId || ''}`}
            className={`text-widget-wrapper ${rest.typographyClass ? rest.typographyClass : ''}`}
            css={css`
                ${!theme ? textStyles : themeTextStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
        >
            <Text
                textInput={boundText}
                truncateTextAt={truncateTextAt}
                disableTabStaticData={globalDisableTabStaticData || rest.disableTabStaticData}
                onTriggerTracking={onTriggerTracking}
                useAnalytics={useAnalytics}
                readMoreText={boundReadMoreText}
                readLessText={boundReadLessText}
            />
        </div>
    ) : (
        <></>
    );
};

export default TextWrapper;
